/* De instellingen van de site (/crm/website/site/instellingen, ADR 0024): contact, de rekentools en
 * de getallenbalk op de homepage. Dit bestand tekent alleen. Welke instellingen er zijn, wat een
 * geldige waarde is en wat er wordt geschreven, staat in data/instellingen.ts.
 *
 * Een instelling die niemand aanraakt, wordt niet geschreven. Heeft een instelling nog geen rij, dan
 * maakt het opslaan hem aan. Er wordt nooit een rij verwijderd. Na het opslaan vraagt de CRM de site
 * om te verversen (ververs.ts, ADR 0031). */

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Save } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useInstellingen, useInstellingenOpslaan, useRechten } from './hooks';
import {
  AANTAL_VAKKEN, ALLE_TOOLS, GROEPEN, SLEUTELS, VAK_ZIN, beginConcept, controleerInstellingen, invoer, staatAan, vakSleutels, vakStaat, wijzigingen,
  type Concept, type Groep, type Instelling, type Stand,
} from './data/instellingen';
import { LATER_INSTELLING, meldOpslag } from './ververs';
import { Fout, Kop } from './WebsiteBeheer';

const TERUG = { naar: '/crm/website/site', label: 'Content op de site' };
const TITEL = 'Instellingen van de site';

export function WebsiteInstellingen() {
  const { magBewerken } = useRechten();
  const { data: stand, error, isLoading } = useInstellingen();

  if (isLoading) return <p className="text-sm text-muted-foreground">Laden…</p>;
  if (error || !stand) {
    return (
      <div className="space-y-4">
        <Kop titel={TITEL} terug={TERUG} />
        <Fout tekst={error ? (error as Error).message : 'De instellingen zijn niet te lezen.'} />
      </div>
    );
  }
  return <Formulier stand={stand} magBewerken={magBewerken} />;
}

interface VakProps { stand: Stand; concept: Concept; fouten: Record<string, string>; uit: boolean; zet: (sleutel: string, waarde: string) => void }

function Tekstvak({ instelling, label, concept, fouten, uit, zet }: VakProps & { instelling: Instelling; label?: string }) {
  const id = `instelling-${instelling.sleutel}`;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label ?? instelling.label}</Label>
      <Input id={id} value={concept[instelling.sleutel] ?? ''} placeholder={instelling.voorbeeld} disabled={uit} onChange={(e) => zet(instelling.sleutel, e.target.value)} />
      {instelling.letOp && (
        <p className="flex items-start gap-1.5 text-xs text-amber-600" data-let-op={instelling.sleutel}>
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span>{instelling.letOp}</span>
        </p>
      )}
      {instelling.uitleg && <p className="text-xs text-muted-foreground">{instelling.uitleg}</p>}
      {fouten[instelling.sleutel] && <p className="text-xs text-destructive" role="alert">{fouten[instelling.sleutel]}</p>}
    </div>
  );
}

function Contact({ groep, ...vak }: VakProps & { groep: Groep }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {groep.instellingen.map((instelling) => (
        // Een vak met een waarschuwing krijgt de volle breedte, zodat de tekst leesbaar blijft.
        <div key={instelling.sleutel} className={instelling.letOp ? 'sm:col-span-2' : undefined}>
          <Tekstvak instelling={instelling} {...vak} />
        </div>
      ))}
    </div>
  );
}

function Rekentools({ groep, stand, concept, fouten, uit, zet }: VakProps & { groep: Groep }) {
  const allesAan = staatAan(concept[ALLE_TOOLS]);
  /* Weer aanzetten geeft de waarde terug die er stond, als die al "aan" betekende. Zo is een
   * schakelaar die uit en weer aan gaat niet aangeraakt, en wordt er niets geschreven. */
  const schakel = (sleutel: string, aan: boolean) => zet(sleutel, !aan ? 'false' : staatAan(invoer(stand, sleutel)) ? invoer(stand, sleutel) : 'true');
  return (
    <div className="space-y-3">
      {groep.instellingen.map((instelling) => {
        const id = `instelling-${instelling.sleutel}`;
        const aan = staatAan(concept[instelling.sleutel]);
        const teltNiet = instelling.sleutel !== ALLE_TOOLS && !allesAan;
        return (
          <div key={instelling.sleutel} className="space-y-1" data-schakelaar={instelling.sleutel} data-aan={aan}>
            <div className="flex items-center gap-3">
              <Switch id={id} checked={aan} disabled={uit} onCheckedChange={(nu) => schakel(instelling.sleutel, nu)} />
              <Label htmlFor={id}>{instelling.label}: {aan ? 'aan' : 'uit'}</Label>
            </div>
            {instelling.uitleg && <p className="text-xs text-muted-foreground">{instelling.uitleg}</p>}
            {teltNiet && aan && <p className="text-xs text-amber-600">Telt nu niet mee: alle rekentools staan uit.</p>}
            {fouten[instelling.sleutel] && <p className="text-xs text-destructive" role="alert">{fouten[instelling.sleutel]}</p>}
          </div>
        );
      })}
    </div>
  );
}

function Getallenbalk({ groep, ...vak }: VakProps & { groep: Groep }) {
  const vakken = Array.from({ length: AANTAL_VAKKEN }, (_, i) => i + 1);
  const af = vakken.filter((n) => vakStaat(vak.concept, n) === 'af').length;
  const vind = (sleutel: string) => groep.instellingen.find((i) => i.sleutel === sleutel)!;
  return (
    <div className="space-y-3">
      <ol className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {vakken.map((n) => {
          const staat = vakStaat(vak.concept, n);
          return (
            <li key={n} className="space-y-3 rounded-md border p-3" data-vak={n} data-vak-staat={staat}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">Vak {n}</span>
                <Badge variant={staat === 'af' ? 'default' : 'secondary'}>{staat === 'af' ? 'Telt mee' : 'Telt niet mee'}</Badge>
              </div>
              <Tekstvak instelling={vind(vakSleutels(n).waarde)} label="Waarde" {...vak} />
              <Tekstvak instelling={vind(vakSleutels(n).bijschrift)} label="Bijschrift" {...vak} />
              <p className="text-xs text-muted-foreground">{VAK_ZIN[staat]}</p>
            </li>
          );
        })}
      </ol>
      <p className="text-sm" data-balk={af}>
        {af === 0 ? 'Geen enkel vak is af: met deze invoer is er geen balk op de homepage.' : `Met deze invoer ${af === 1 ? 'staat er 1 vak' : `staan er ${af} vakken`} in de balk.`}
      </p>
      <p className="text-xs text-muted-foreground">
        De waarde is het getal zoals het op de site staat, met de eenheid erbij: getal, spatie, eenheid. Het bijschrift is kort en begint zonder hoofdletter.
        Gewone tekst, geen HTML. De vakken staan op de site in de volgorde van de nummers.
      </p>
    </div>
  );
}

const INHOUD: Record<Groep['sleutel'], (props: VakProps & { groep: Groep }) => JSX.Element> = {
  contact: Contact,
  rekentools: Rekentools,
  getallenbalk: Getallenbalk,
};

function Formulier({ stand, magBewerken }: { stand: Stand; magBewerken: boolean }) {
  const { toast } = useToast();
  const [concept, setConcept] = useState<Concept>(() => beginConcept(stand));
  const opslaan = useInstellingenOpslaan();
  const bezig = opslaan.isPending;
  const uit = !magBewerken || bezig;

  /* Alleen overnemen als de database iets anders zegt (na opslaan). Een verversing op de achtergrond
   * met dezelfde inhoud mag niet weggooien wat iemand net heeft getypt. */
  const versie = useMemo(() => JSON.stringify(SLEUTELS.map((s) => [s in stand, stand[s] ?? null])), [stand]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setConcept(beginConcept(stand)); }, [versie]);

  const controle = useMemo(() => controleerInstellingen(stand, concept), [stand, concept]);
  const teBewaren = useMemo(() => wijzigingen(stand, concept), [stand, concept]);
  const zet = (sleutel: string, waarde: string) => setConcept((c) => ({ ...c, [sleutel]: waarde }));

  const bewaar = async () => {
    try {
      const uitkomst = await opslaan.mutateAsync({ stand, concept });
      const aantal = uitkomst.geschreven.length;
      if (!uitkomst.verversing) {
        toast({ title: 'Niets gewijzigd', description: 'Er is niets opgeslagen.' });
        return;
      }
      // De melding staat er meteen; of de site al ververst is, komt er even later bij.
      void meldOpslag(toast, uitkomst.verversing, aantal === 1 ? 'Instelling opgeslagen' : `${aantal} instellingen opgeslagen`, '', LATER_INSTELLING);
    } catch (fout) {
      toast({ title: 'Niet opgeslagen', description: fout instanceof Error ? fout.message : String(fout), variant: 'destructive' });
    }
  };

  const knop = (
    <Button onClick={bewaar} disabled={bezig || teBewaren.length === 0 || !controle.ok} size="sm">
      <Save className="mr-1 h-4 w-4" /> {bezig ? 'Opslaan…' : 'Opslaan'}
    </Button>
  );

  return (
    <div className="space-y-4">
      <Kop titel={TITEL} terug={TERUG}>{magBewerken && knop}</Kop>
      <p className="max-w-3xl text-sm text-muted-foreground">
        Deze instellingen gelden voor de hele site. Een wijziging staat direct op de site, en anders na tien tot twintig minuten. Wat je niet aanraakt, blijft zoals het is.
        Een vak leegmaken haalt de waarde weg.
      </p>
      {!magBewerken && <Fout tekst="Je kunt de instellingen lezen, maar niet wijzigen." />}

      {GROEPEN.map((groep) => {
        const Inhoud = INHOUD[groep.sleutel];
        const gewijzigd = groep.instellingen.some((i) => teBewaren.some((w) => w.sleutel === i.sleutel) || controle.fouten[i.sleutel]);
        return (
          <Card key={groep.sleutel} data-groep={groep.sleutel} data-groep-staat={gewijzigd ? 'gewijzigd' : 'ongewijzigd'}>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">{groep.titel}</CardTitle>
                {gewijzigd && <Badge variant="secondary">Gewijzigd, nog niet opgeslagen</Badge>}
              </div>
              <p className="text-sm text-muted-foreground">{groep.uitleg}</p>
            </CardHeader>
            <CardContent>
              <Inhoud groep={groep} stand={stand} concept={concept} fouten={controle.fouten} uit={uit} zet={zet} />
            </CardContent>
          </Card>
        );
      })}

      {/* Het formulier is lang: onderaan staat de knop er nog een keer. */}
      {magBewerken && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {!controle.ok && <p className="text-xs text-destructive">{Object.values(controle.fouten)[0]}</p>}
          {knop}
        </div>
      )}
    </div>
  );
}
